<template>
  <section class="magic-workspace">
    <section class="magic-pane">
      <header><div><h3>Draft a response</h3><p>Paste a message or describe the response you need.</p></div><button :disabled="generating || saving" @click="newChat">New chat</button></header>
      <div ref="chatFeed" class="pane-feed" aria-live="polite">
        <p v-if="!chat.length" class="empty">Ask for a reusable reply, then follow up to refine it. Chat stays here for this session; saved responses appear in the library.</p>
        <article v-for="item in chat" :key="item.id" :class="['chat-message', item.role]">
          <p v-if="item.role === 'user'" class="message-body">{{ item.content }}</p>
          <template v-else>
            <label class="subject-bubble">Subject<input v-model="item.title" maxlength="120" :disabled="!!item.pending || item.saved"></label>
            <label>Response<textarea v-model="item.body" rows="8" maxlength="10000" :disabled="!!item.pending || item.saved" /></label>
            <fieldset :disabled="!!item.pending || item.saved"><legend>Request statuses · select any that apply</legend>
              <div class="capsule-strip" role="group" aria-label="Request statuses">
                <button v-for="option in item.statusOptions" :key="option.value" type="button" class="choice-capsule" :aria-pressed="item.statuses.includes(option.value)" @click="toggleChoice(item, 'statuses', option.value)">{{ option.label }}</button>
              </div>
            </fieldset>
            <fieldset :disabled="!!item.pending || item.saved"><legend>Tags · select any to include</legend>
              <div class="capsule-strip" role="group" aria-label="Suggested tags">
                <button v-for="tag in item.tagOptions" :key="tag" type="button" class="choice-capsule" :aria-pressed="item.tags.includes(tag)" @click="toggleChoice(item, 'tags', tag)">{{ tag }}</button>
                <small v-if="!item.tagOptions.length">No tags suggested.</small>
              </div>
            </fieldset>
            <div class="actions"><button @click="copy(item.body)">Copy response</button><button :disabled="!!saving || item.saved || !item.body.trim() || !item.title.trim()" @click="save(item)">{{ item.saved ? 'Saved' : saving === item.id ? 'Saving…' : item.pending ? 'Retry save' : 'Save response' }}</button></div>
          </template>
        </article>
        <p v-if="generating" role="status">Drafting your response…</p>
      </div>
      <form class="chat-composer" @submit.prevent="ask">
        <label for="magic-prompt">Your instruction</label><textarea id="magic-prompt" v-model="prompt" rows="3" maxlength="12000" :disabled="generating" placeholder="Draft a follow-up for an attorney request that received no reply…" />
        <div class="actions"><small>{{ prompt.length }}/12,000</small><button :disabled="generating || !prompt.trim()">{{ generating ? 'Drafting…' : 'Ask Maix Magic' }}</button></div>
        <p v-if="chatError" role="alert" class="error">{{ chatError }}</p><p v-if="notice" role="status">{{ notice }}</p>
      </form>
    </section>
    <section class="magic-pane">
      <header><div><h3>Saved responses</h3><p>Reusable wording for common client messages.</p></div></header>
      <form class="library-search" @submit.prevent="searchLibrary">
        <label>Search responses<input v-model="search" maxlength="200" placeholder="Search words, tags, or status"></label>
        <div class="search-row"><label>Request status<select v-model="status"><option value="">All statuses</option><option v-for="option in statuses" :key="option.value" :value="option.value">{{ option.label }}</option></select></label><button :disabled="loading">{{ loading ? 'Searching…' : 'Search / Refresh' }}</button></div>
      </form>
      <div class="pane-feed" aria-live="polite">
        <p v-if="libraryError" class="error" role="alert">{{ libraryError }}</p>
        <p v-if="!loading && !responses.length && !libraryError" class="empty">No saved responses match this search.</p>
        <article v-for="response in responses" :key="response.id" class="saved-response">
          <h4>{{ response.title }}</h4><p class="message-body">{{ response.body }}</p>
          <div class="pills"><span v-for="value in response.statuses" :key="value">{{ statusLabel(value) }}</span><span v-for="tag in response.tags" :key="'tag-' + tag">#{{ tag }}</span></div>
          <div class="actions"><button @click="copy(response.body)">Copy response</button><button :disabled="generating || !!saving" @click="useResponse(response)">Refine in chat</button></div>
        </article>
        <button v-if="cursor" :disabled="loading" @click="loadLibrary(true)">{{ loading ? 'Loading…' : 'Load more matches' }}</button>
      </div>
      <p v-if="copyError" class="error" role="alert">{{ copyError }}</p>
    </section>
  </section>
</template>
<script>
import { ref, nextTick, onMounted, onBeforeUnmount } from "vue";
import { generateMaixMagicResponse, saveMaixMagicResponse, listMaixMagicResponses } from "@/firebase/firestore";
const statuses = [ { value: "new", label: "New" }, { value: "reviewing", label: "Reviewing" }, { value: "needs-information", label: "Needs Information" }, { value: "referred", label: "Referred" }, { value: "accepted", label: "Accepted" }, { value: "closed", label: "Closed" } ];
export default {
  name: "MaixMagicDashboard",
  setup() {
    const chat = ref([]), prompt = ref(""), generating = ref(false), saving = ref("");
    const chatError = ref(""), notice = ref(""), copyError = ref(""), chatFeed = ref(null);
    const responses = ref([]), search = ref(""), status = ref(""), cursor = ref(null), loading = ref(false), libraryError = ref("");
    let disposed = false, version = 0, filters = { search: "", status: "" };
    const statusLabel = value => statuses.find(item => item.value === value)?.label || value;
    const scrollChat = async () => { await nextTick(); if (chatFeed.value) chatFeed.value.scrollTop = chatFeed.value.scrollHeight; };
    const assistant = response => ({ ...response, statuses: [...response.statuses], tags: [...response.tags],
      id: crypto.randomUUID(), role: "assistant", tagOptions: [...response.tags],
      statusOptions: [...statuses.filter(option => response.statuses.includes(option.value)), ...statuses.filter(option => !response.statuses.includes(option.value))],
      saved: false, pending: null });
    const toggleChoice = (item, field, value) => {
      if (item.pending || item.saved) return;
      item[field] = item[field].includes(value) ? item[field].filter(choice => choice !== value) : [...item[field], value];
    };
    const ask = async () => {
      if (generating.value || !prompt.value.trim()) return;
      const content = prompt.value.trim();
      const messages = [...chat.value.map(item => ({ role: item.role, content: item.role === "user" ? item.content : item.body })), { role: "user", content }];
      generating.value = true; chatError.value = ""; notice.value = "";
      try {
        const result = await generateMaixMagicResponse(messages);
        if (disposed) return;
        chat.value.push({ id: crypto.randomUUID(), role: "user", content }, assistant(result));
        prompt.value = ""; await scrollChat();
      } catch (error) { if (!disposed) chatError.value = error.message || "Unable to draft a response."; }
      finally { generating.value = false; }
    };
    const loadLibrary = async (more = false) => {
      const current = ++version;
      loading.value = true; libraryError.value = "";
      if (!more) { responses.value = []; cursor.value = null; }
      let next = more ? cursor.value : null;
      const found = [];
      try {
        do {
          const page = await listMaixMagicResponses({ ...filters, cursor: next });
          if (disposed || current !== version) return;
          found.push(...page.responses); next = page.cursor;
        } while (next && found.length < 30);
        responses.value = more ? [...responses.value, ...found] : found;
        cursor.value = next;
      } catch (error) { if (!disposed && current === version) libraryError.value = error.message || "Unable to load saved responses."; }
      finally { if (current === version) loading.value = false; }
    };
    const searchLibrary = () => { filters = { search: search.value.trim(), status: status.value }; return loadLibrary(); };
    const save = async item => {
      if (saving.value || item.saved) return;
      saving.value = item.id; chatError.value = ""; notice.value = "";
      const payload = item.pending || { id: item.id, title: item.title, body: item.body, statuses: [...item.statuses], tags: [...item.tags] };
      item.pending = payload;
      try {
        await saveMaixMagicResponse(payload);
        if (disposed) return;
        item.saved = true; item.pending = null; notice.value = "Response saved to the library.";
        search.value = ""; status.value = ""; await searchLibrary();
      } catch (error) {
        if (error.code === "functions/invalid-argument") item.pending = null;
        if (!disposed) chatError.value = error.message || "Unable to confirm save. Retry uses the same response ID.";
      } finally { saving.value = ""; }
    };
    const copy = async text => {
      copyError.value = "";
      try { await navigator.clipboard.writeText(text); notice.value = "Response copied."; }
      catch { copyError.value = "Copy failed. Select the response text and copy it manually."; }
    };
    const newChat = () => { chat.value = []; prompt.value = ""; chatError.value = ""; notice.value = ""; };
    const useResponse = response => {
      chat.value.push({ id: crypto.randomUUID(), role: "user", content: "Use this saved response as the starting draft." }, assistant(response));
      prompt.value = "Revise this response to "; scrollChat();
    };
    onMounted(searchLibrary);
    onBeforeUnmount(() => { disposed = true; version++; });
    return { toggleChoice, statuses, statusLabel, chat, prompt, generating, saving, chatError, notice, copyError, chatFeed, responses, search, status, cursor, loading, libraryError, ask, save, copy, newChat, useResponse, searchLibrary, loadLibrary };
  }
};
</script>
<style scoped>
.magic-workspace { height: 100%; min-height: 0; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; }
.magic-pane { min-height: 0; min-width: 0; display: flex; flex-direction: column; border: 1px solid #dce5e1; border-radius: 16px; background: white; overflow: hidden; }
header { padding: 18px; display: flex; justify-content: space-between; gap: 12px; align-items: center; border-bottom: 1px solid #e3e9e6; }
h3, h4 { margin: 0; color: #294236; } header p, .empty { color: #75867d; font-size: .85rem; } header p { margin: 6px 0 0; }
.pane-feed { flex: 1; min-height: 120px; overflow-y: auto; padding: 16px; }
.chat-message, .saved-response { border-radius: 12px; padding: 14px; margin-bottom: 14px; background: #f3f7f4; overflow-wrap: anywhere; }
.chat-message.user { background: #e4f2e9; margin-left: 24px; }
.message-body { white-space: pre-wrap; }
label { display: block; font-size: .85rem; color: #40544a; font-weight: 600; }
input:not([type=checkbox]), textarea, select { width: 100%; box-sizing: border-box; display: block; border: 1px solid #d1dfd7; border-radius: 8px; padding: 9px; font: inherit; margin: 5px 0 12px; background: white; }
textarea { resize: vertical; } fieldset { border: 0; margin: 10px 0; padding: 0; } legend { font-size: .85rem; font-weight: 600; }
 .subject-bubble { padding: 10px 12px; margin-bottom: 12px; border-radius: 12px; background: #e4eee8; }
.subject-bubble input { margin-bottom: 0; }
.capsule-strip { display: flex; flex-wrap: nowrap; gap: 8px; overflow-x: auto; padding: 4px 0 8px; }
button.choice-capsule { flex: 0 0 auto; white-space: nowrap; border-radius: 24px; background: #e8ece9; color: #65736a; }
button.choice-capsule[aria-pressed="true"] { background: #16794f; color: white; }
.choice-capsule:focus-visible { outline: 2px solid #294236; outline-offset: 2px; }
button { border: 0; border-radius: 8px; padding: 9px 13px; background: #16794f; color: white; cursor: pointer; font: inherit; font-size: .8rem; } button:disabled { opacity: .5; cursor: default; }
.actions, .search-row { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; }.search-row label { flex: 1; }
.chat-composer, .library-search { padding: 16px; border-top: 1px solid #e3e9e6; flex: 0 0 auto; }.chat-composer { max-height: 40%; overflow: auto; }
.pills { display: flex; flex-wrap: wrap; gap: 6px; margin: 12px 0; }.pills span { border-radius: 20px; padding: 4px 8px; font-size: .75rem; color: #315d45; background: #e2eee6; }
.error { color: #a52838; } small { color: #75867d; }
@media (max-width: 900px) { .magic-workspace { grid-template-columns: 1fr; height: auto; } .magic-pane { min-height: 500px; max-height: 850px; } }
</style>
